import { Link } from "react-router-dom";
import { ArrowLeft, Shield, FileText, Lock, Eye, Cookie, UserCheck, RefreshCw, Mail } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useEffect } from "react";

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const isArabic = language === "ar";

  useEffect(() => {
    document.title = isArabic
      ? "سياسة الخصوصية - مؤسسة العربية الواحة"
      : "Privacy Policy - Arabian Al Waha Est";
    window.scrollTo(0, 0);
  }, [isArabic]);

  const t = {
    back: isArabic ? "العودة إلى الموقع" : "Back to website",
    title: isArabic ? "سياسة الخصوصية" : "Privacy Policy",
    subtitle: isArabic
      ? "آخر تحديث: 15 مايو 2026"
      : "Last updated: May 15, 2026",
    intro: isArabic
      ? "تلتزم مؤسسة العربية الواحة بحماية خصوصيتك. توضح هذه السياسة كيف نجمع المعلومات ونستخدمها ونحميها عند زيارتك لموقعنا أو التواصل معنا."
      : "Arabian Al Waha Est is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your information when you visit our website or communicate with us.",
    sections: [
      {
        icon: Eye,
        title: isArabic ? "المعلومات التي نجمعها" : "Information We Collect",
        content: isArabic
          ? "قد نجمع معلومات الاتصال الأساسية مثل الاسم وعنوان البريد الإلكتروني ورقم الهاتف واسم الشركة عندما تملأ نموذج الاتصال أو تطلب عرض سعر. كما نجمع بيانات الاستخدام المجهولة لتجربة الموقع فقط."
          : "We may collect basic contact information such as your name, email address, phone number, and company name when you fill out our contact form or request a quote. We also collect anonymous usage data to improve your site experience.",
      },
      {
        icon: FileText,
        title: isArabic ? "كيف نستخدم معلوماتك" : "How We Use Your Information",
        content: isArabic
          ? "نستخدم معلوماتك للرد على استفساراتك ومعالجة طلبات عروض الأسعار والتواصل بشأن خدماتنا. لا نبيع أو نشارك بياناتك مع أطراف ثالثة لأغراض تسويقية."
          : "We use your information to respond to your inquiries, process quote requests, and communicate about our services. We do not sell or share your data with third parties for marketing purposes.",
      },
      {
        icon: Lock,
        title: isArabic ? "حماية البيانات" : "Data Protection",
        content: isArabic
          ? "نستخدم تدابير أمنية صناعية قياسية لحماية معلوماتك من الوصول غير المصرح به أو التغيير أو الإفصاح. تشمل هذه التدابير تشفير البيانات أثناء النقل وحماية الوصول للخوادم."
          : "We employ industry-standard security measures to protect your information from unauthorized access, alteration, or disclosure. This includes data encryption during transmission and access-controlled server infrastructure.",
      },
      {
        icon: Cookie,
        title: isArabic ? "ملفات تعريف الارتباط" : "Cookies & Tracking",
        content: isArabic
          ? "يستخدم موقعنا ملفات تعريف الارتباط الأساسية لضمان الأداء والوظائف الأساسية. لا نستخدم تقنيات التتبع التسويقية أو التحليلية التي تنتهك خصوصيتك."
          : "Our site uses essential cookies to ensure performance and core functionality. We do not use marketing or invasive analytics tracking technologies that compromise your privacy.",
      },
      {
        icon: Shield,
        title: isArabic ? "الخدمات الخارجية" : "Third-Party Services",
        content: isArabic
          ? "نستخدم خدمات موثوقة لإرسال البريد الإلكتروني (Resend) واستضافة الخلفية (Lovable Cloud). هذه الخدمات خاضعة لسياسات الخصوصية الخاصة بها ولا تستخدم بياناتك إلا لتقديم خدماتنا."
          : "We use trusted services for email delivery (Resend) and backend hosting (Lovable Cloud). These services are bound by their own privacy policies and only process your data to deliver our services.",
      },
      {
        icon: UserCheck,
        title: isArabic ? "حقوقك" : "Your Rights",
        content: isArabic
          ? "لديك الحق في الوصول إلى بياناتك الشخصية التي نحتفظ بها، وطلب تصحيحها أو حذفها. للاستفسارات المتعلقة بالخصوصية، يرجى التواصل معنا عبر البريد الإلكتروني."
          : "You have the right to access the personal data we hold about you, and to request correction or deletion. For privacy-related inquiries, please contact us via email.",
      },
      {
        icon: RefreshCw,
        title: isArabic ? "تحديثات السياسة" : "Policy Updates",
        content: isArabic
          ? "قد نقوم بتحديث هذه السياسة من وقت لآخر. سيتم نشر أي تغييرات على هذه الصفحة مع تاريخ المراجعة المحدث. نوصي بمراجعة هذه السياسة دورياً."
          : "We may update this policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.",
      },
      {
        icon: Mail,
        title: isArabic ? "اتصل بنا" : "Contact Us",
        content: isArabic
          ? "لأي أسئلة أو استفسارات حول سياسة الخصوصية هذه، يرجى التواصل معنا على: sales@arabianalwaha.com"
          : "For any questions or concerns about this privacy policy, please contact us at: sales@arabianalwaha.com",
      },
    ],
    footerNote: isArabic
      ? "باستخدامك لموقعنا، فإنك توافق على سياسة الخصوصية هذه."
      : "By using our website, you consent to this privacy policy.",
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isArabic ? "rtl" : "ltr"}>
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/40">
        <div className="container mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary to-primary/80">
        <div className="container mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <Shield className="w-12 h-12 text-secondary mx-auto mb-6" />
            <h1 className="text-white font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl leading-tight mb-4">
              {t.title}
            </h1>
            <p className="text-white/70 text-sm">{t.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-6 lg:px-10 max-w-3xl">
          <p className="text-lg text-muted-foreground leading-relaxed text-center">
            {t.intro}
          </p>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="pb-20 lg:pb-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <div
                key={index}
                className="bg-card rounded-xl p-6 lg:p-8 shadow-soft border border-border/40 hover:shadow-elegant transition-elegant"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center shrink-0">
                    <section.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-accent mb-3">
                      {section.title}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground">{t.footerNote}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
